'use client'

import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'

import { useIntl } from 'react-intl'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { NestableAnalyticsProvider } from '@b-stock/fe-core/bstock-react'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import ListingCard from '@/components/listings/listingCard/ListingCard'
import { Grid } from '@/components/listings/ListingGrid/ListingGrid'
import { FiltersList } from '@/components/listings/search/AppliedFilters/FiltersList'
import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { useAllListingsFilters } from '@/components/listings/search/SearchFilters'
import {
  summarizeDealBadgeImpressions,
  toDealBadgeSegmentProps,
} from '@/helpers/deals/dealBadgeSegmentProps'
import { resolveDealBadge } from '@/helpers/deals/resolveDealBadge'
import { FeatureFlags } from '@/helpers/featureFlags'
import { toBidderCountSegmentProps } from '@/helpers/listing/bidderCount'
import { getPromotedAnalyticsFields } from '@/helpers/telemetry/promotedListingAnalytics'
import { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useDealBadgeFlags } from '@/hooks/useDealBadgeFlags'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'
import { SIMILAR_SEARCH_LIMIT } from '@/queries/similarSearchQuery'

import { computeSimilarInventoryFilters } from './relaxedFilters'
import {
  SectionContainer,
  SectionHeader,
  SectionTitle,
  ChipsRow,
  ShowAllCard,
  ShowAllCardText,
  ShowAllCardTitle,
  ShowAllCardSubtitle,
  ShowAllButton,
} from './ui'

const SimilarInventory = ({
  storefront,
  scrollRef,
}: {
  storefront?: StoreFrontDto
  scrollRef?: RefObject<HTMLDivElement | null>
}) => {
  const { formatMessage } = useIntl()
  const { similarSearch, state, updateFilters } = useListingSearch()
  const { filters: filterComponents } = useAllListingsFilters(storefront)
  const { track, trackButtonClicked } = useAnalytics()
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const dealBadgeFlags = useDealBadgeFlags()
  const { data: newSellerIds } = newSellerStorefrontsQuery.useWithOpts([], {
    enabled: isNewSellerTreatment,
  })

  const results = similarSearch.data?.listings ?? []
  const hasResults = results.length > 0
  const reasonShown = similarSearch.reasonShown ?? 'end_of_served_listings'
  const isZeroResultsShelf = reasonShown === 'zero_results'

  const { remainingFilters, appliedFilterKeys, removedFilterKeys } = useMemo(
    () =>
      computeSimilarInventoryFilters(
        similarSearch.data?.modifications.removedFilters ?? [],
        state.filters
      ),
    [similarSearch.data, state.filters]
  )

  const screenName = storefront
    ? ScreenName.SellerStorefront
    : ScreenName.AllAuction

  // On an empty result set with nothing relaxed — a keyword-only search, where
  // there are no filters to drop — "show more" would re-run the identical
  // search the buyer is already looking at.
  const showAll =
    (similarSearch.data?.total ?? 0) > SIMILAR_SEARCH_LIMIT &&
    (!isZeroResultsShelf || removedFilterKeys.length > 0)

  // The impression fires on a 3s timer whose effect deliberately watches only
  // the fetch lifecycle. Read the reason at fire time so a shelf that changes
  // placement mid-timer reports the placement it actually rendered.
  const reasonRef = useRef(reasonShown)
  reasonRef.current = reasonShown

  // Clicks here must carry the shelf's impression id, not the grid's, or the
  // impression and the click cannot be joined. Published even when null:
  // omitting the key would inherit the grid's id from the surrounding search
  // provider, so a null here deliberately clobbers it. Anything tracked from
  // this component's own useAnalytics sits ABOVE this provider and has to set
  // the key itself — see handleShowAll.
  const [shelfImpressionId, setShelfImpressionId] = useState<string | null>(
    null
  )

  // Fire once per completed fetch. isFetching flips on filter changes but not on
  // Pusher updates, so Pusher never re-triggers this timer. The reset runs on
  // every re-run so a stale id never outlives the results it described.
  useEffect(() => {
    setShelfImpressionId(null)

    if (!similarSearch.enabled || similarSearch.isFetching || !hasResults) {
      return
    }

    const impressionId = crypto.randomUUID()
    const timer = setTimeout(() => {
      setShelfImpressionId(impressionId)
      const cards = results.map((listing, i) => ({
        id: listing.id,
        position: i + 1,
        is_new_seller:
          isNewSellerTreatment &&
          (newSellerIds?.has(listing.storefrontId) ?? false),
        ...getPromotedAnalyticsFields(listing),
        // No `rendered` option: this shelf renders its own Grid in either view
        // (in List view, only as the zero-results shelf).
        ...toDealBadgeSegmentProps(
          resolveDealBadge(listing, screenName, dealBadgeFlags)
        ),
        ...toBidderCountSegmentProps(listing),
      }))
      track('listing_card_impression', {
        listing_card_impression_id: impressionId,
        listings_count: results.length,
        carousel_name: 'similar_inventory',
        screen_name: screenName,
        active_filters: appliedFilterKeys,
        removed_filters: removedFilterKeys,
        reason_shown: reasonRef.current,
        ...summarizeDealBadgeImpressions(cards),
        listings: cards,
      })
    }, 3000)

    return () => clearTimeout(timer)
    // oxlint-disable-next-line react/exhaustive-deps
  }, [similarSearch.enabled, similarSearch.isFetching, hasResults])

  if (!similarSearch.enabled || !hasResults) {
    return null
  }

  const handleShowAll = () => {
    trackButtonClicked({
      buttonName: 'show_more_listings',
      carousel_name: 'similar_inventory',
      screen_name: screenName,
      active_filters: appliedFilterKeys,
      removed_filters: removedFilterKeys,
      listing_card_impression_id: shelfImpressionId,
      // Fires above the provider below, so it cannot inherit the reason.
      reason_shown: reasonShown,
    })
    scrollRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    updateFilters(remainingFilters)
  }

  return (
    <NestableAnalyticsProvider
      value={{
        carousel_name: 'similar_inventory',
        active_filters: appliedFilterKeys,
        removed_filters: removedFilterKeys,
        listing_card_impression_id: shelfImpressionId,
        reason_shown: reasonShown,
      }}
    >
      <SectionContainer data-testid="similar-inventory-section">
        <SectionHeader>
          <SectionTitle>
            {formatMessage({
              id: isZeroResultsShelf
                ? 'Listings.similarInventory.noResultsTitle'
                : 'Listings.similarInventory.title',
            })}
          </SectionTitle>
        </SectionHeader>

        <ChipsRow data-testid="similar-inventory-chips">
          <FiltersList
            filterComponents={filterComponents}
            filtersState={remainingFilters}
            updateFilters={() => {}} // noop, chips are read-only — must not mutate search state
            noClose
          />
        </ChipsRow>

        <Grid>
          {results.map((listing, idx) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              position={idx + 1}
              screenName={screenName}
            />
          ))}
          {showAll && (
            <ShowAllCard data-testid="similar-inventory-show-all">
              <ShowAllCardText>
                <ShowAllCardTitle>
                  {formatMessage({
                    id: 'Listings.similarInventory.showAll.title',
                  })}
                </ShowAllCardTitle>
                <ShowAllCardSubtitle>
                  {formatMessage({
                    id: 'Listings.similarInventory.showAll.subtitle',
                  })}
                </ShowAllCardSubtitle>
              </ShowAllCardText>
              <ShowAllButton
                appearance="primary"
                size="large"
                onClick={handleShowAll}
              >
                {formatMessage({
                  id: 'Listings.similarInventory.showAll.button',
                })}
              </ShowAllButton>
            </ShowAllCard>
          )}
        </Grid>
      </SectionContainer>
    </NestableAnalyticsProvider>
  )
}

export default SimilarInventory
